/**
 * Client for the `phone-number` edge function. No component talks to the
 * `phone_numbers` table, or to the LiveKit / Twilio set-up functions, directly.
 * Field names are the column names.
 *
 * A Twilio number is imported (`/twilio`) and then pointed at Verbeo inside the
 * customer's Twilio account (`/:id/twilio-webhook`); a SIP-trunk number is
 * connected in one call (`/sip`), which also creates its LiveKit trunks. Keep in
 * sync with `supabase/functions/phone-number/` and `utils/phone-number-rules.ts`.
 */

import { api } from "@/lib/api-client";
import { type ListParams, MAX_PAGE_SIZE, type PagedResult } from "@/lib/list-query";

const BASE = "/phone-number";

export type SipTransport = "auto" | "udp" | "tcp" | "tls";

/** A `phone_numbers` row as the function returns it. The Twilio auth token is
 * write-only and the LiveKit trunk ids are internal, so neither is read back. */
export interface PhoneNumber {
  id: string;
  workspace_id: string;
  /** E.164, e.g. "+14155550123". */
  phone_number: string;
  nickname: string | null;
  provider: "twilio" | "livekit_sip";
  is_active: boolean;
  max_cps: number;
  inbound_agent_id: string | null;
  outbound_agent_id: string | null;
  /** Null when unassigned, or when the agent is not in this number's workspace. */
  inbound_agent_name: string | null;
  outbound_agent_name: string | null;
  inbound_webhook_url: string | null;
  created_at: string;
  updated_at: string;
}

interface NewNumber {
  workspace_id: string;
  /** E.164. */
  phone_number: string;
  nickname?: string | null;
  inbound_agent_id?: string | null;
  outbound_agent_id?: string | null;
}

export interface ImportTwilioNumberBody extends NewNumber {
  twilio_sid: string;
  /** Write-only. */
  twilio_auth_token: string;
}

export interface ConnectSipNumberBody extends NewNumber {
  termination_uri: string;
  transport?: SipTransport;
  sip_username?: string | null;
  sip_password?: string | null;
}

/** A partial update; at least one field. `inbound_webhook_url` is a SIP-trunk number's only. */
export type UpdatePhoneNumberBody = Partial<
  Pick<PhoneNumber, "inbound_agent_id" | "outbound_agent_id" | "max_cps" | "inbound_webhook_url">
>;

/** Where Twilio now sends the number's calls and their status. */
export interface TwilioWebhook {
  voice_url: string;
  status_callback_url: string;
}

/** One page of the workspace's numbers, newest first. */
export const listPhoneNumbers = (workspaceId: string, params: ListParams) =>
  api.postPage<PhoneNumber>(`${BASE}/list`, params, { workspace_id: workspaceId });

/**
 * Every number of the workspace, a page of the server's ceiling at a time, until
 * it holds the whole set — or a page comes back empty, so a total that shrinks
 * mid-read cannot loop forever.
 */
export async function listAllPhoneNumbers(workspaceId: string): Promise<PhoneNumber[]> {
  const numbers: PhoneNumber[] = [];
  let page = 1;
  let last: PagedResult<PhoneNumber>;
  do {
    last = await listPhoneNumbers(workspaceId, { page: page++, size: MAX_PAGE_SIZE, search: "" });
    numbers.push(...last.rows);
  } while (last.rows.length > 0 && numbers.length < last.total);
  return numbers;
}

/** Stores the number only: Twilio is not called until `configureTwilioWebhook`. */
export const importTwilioNumber = (body: ImportTwilioNumberBody) =>
  api.post<PhoneNumber>(`${BASE}/twilio`, body);

/** Also creates the number's LiveKit trunks and dispatch rule, server-side. */
export const connectSipNumber = (body: ConnectSipNumberBody) =>
  api.post<PhoneNumber>(`${BASE}/sip`, body);

export const updatePhoneNumber = (id: string, body: UpdatePhoneNumberBody) =>
  api.put<PhoneNumber>(`${BASE}/${id}`, body);

/** Campaigns and call history are kept: they just no longer name a number. A
 * campaign that can still dial from it refuses the delete (409). */
export const deletePhoneNumber = (id: string) => api.del(`${BASE}/${id}`);

/** Points the number at Verbeo inside the customer's own Twilio account. */
export const configureTwilioWebhook = (id: string) =>
  api.post<TwilioWebhook>(`${BASE}/${id}/twilio-webhook`, {});
